import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  HStack,
  Link,
  VStack,
  Divider,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  FaWhatsapp,
  FaLinkedinIn,
  FaFacebookF,
  FaUpload,
} from "react-icons/fa";

const CareersFooter = () => {
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isJobModalOpen,
    onOpen: onJobModalOpen,
    onClose: onJobModalClose,
  } = useDisclosure();
  const toast = useToast();

  // Determine if we're on skill development or course details page
  const isSkillDevPage =
    location.pathname.includes("/skill-development") ||
    location.pathname.includes("/course-details");

  // Set content based on page
  const headerText = isSkillDevPage
    ? "Subscribe For Course Announcement"
    : "Subscribe For Job Announcement";
  const descriptionText = isSkillDevPage
    ? "Join our course alert list and be the first to know about new training opportunities"
    : "Join our job alert list and be the first to know about new opportunities";
  const buttonText = isSkillDevPage
    ? "Subscribe Course Announcement"
    : "Subscribe Job Announcement";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [jobFormData, setJobFormData] = useState({
    name: "",
    email: "",
    phone: "",
    positions: [], // Changed from position to positions array
    cv: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isJobSubmitting, setIsJobSubmitting] = useState(false);

  // Available position options
  const positionOptions = [
    "Software Developer",
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "UI/UX Designer",
    "Project Manager",
    "Quality Assurance",
    "DevOps Engineer",
    "Business Analyst",
    "Digital Marketing Specialist",
    "Data Scientist",
    "Mobile App Developer",
    "System Administrator",
    "Cybersecurity Specialist",
    "Technical Writer",
    "Product Manager",
    "Sales Executive",
    "HR Specialist",
    "Graphic Designer",
    "Other",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleJobInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "cv" && files) {
      setJobFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    } else if (name === "positions") {
      // Handle position selection
      const selectedPosition = value;
      if (
        selectedPosition &&
        !jobFormData.positions.includes(selectedPosition)
      ) {
        setJobFormData((prev) => ({
          ...prev,
          positions: [...prev.positions, selectedPosition],
        }));
        // Reset the select value to placeholder
        e.target.value = "";
      }
    } else {
      setJobFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const removePosition = (positionToRemove) => {
    setJobFormData((prev) => ({
      ...prev,
      positions: prev.positions.filter((pos) => pos !== positionToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      toast({
        title: "Successfully Subscribed!",
        description: "Successfully Subscribed!",
        status: "success",
        duration: 1000,
        isClosable: true,
      });

      // reload after 2 seconds (after toast is visible)
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      return;
      const CMS_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:1337/api";

      // Call Strapi subscription endpoint
      const response = await fetch(
        `${CMS_BASE_URL}/external-data/job-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
          }),
        }
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // If response is not JSON, get text
          const errorText = await response.text();
          errorMessage = errorText.substring(0, 200);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Successfully Subscribed!",
          description: `A confirmation link has been sent to your email (${formData.email}).`,
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setFormData({ name: "", email: "", phone: "" });
        onClose();
      } else {
        throw new Error(result.message || "Failed to subscribe");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast({
        title: "Subscription Failed",
        description: error?.message || "Server error. Please try again later.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJobSubmit = async (e) => {
    e.preventDefault();
    setIsJobSubmitting(true);

    try {
      // Validate required fields
      if (!jobFormData.name || !jobFormData.email || !jobFormData.phone) {
        throw new Error("Please fill in all required fields");
      }

      if (
        !jobFormData.positions ||
        !Array.isArray(jobFormData.positions) ||
        jobFormData.positions.length === 0
      ) {
        throw new Error("Please select at least one interested position");
      }

      if (!jobFormData.cv) {
        throw new Error("Please upload your CV file");
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", jobFormData.name);
      formData.append("email", jobFormData.email);
      formData.append("phone", jobFormData.phone);
      formData.append("positions", JSON.stringify(jobFormData.positions)); // Send as JSON array
      formData.append("cv", jobFormData.cv);

      const CMS_BASE_URL = process.env.REACT_APP_API_URL;

      // Call CV drop endpoint via Strapi CMS
      const response = await fetch(`${CMS_BASE_URL}/external-data/cv-drop`, {
        method: "POST",
        body: formData, // Don't set Content-Type header for FormData
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          const errorText = await response.text();
          errorMessage = errorText.substring(0, 200);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        toast({
          title: "CV Submitted Successfully!",
          description:
            "Your CV has been submitted successfully. We'll contact you soon regarding your application.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setJobFormData({
          name: "",
          email: "",
          phone: "",
          positions: [],
          cv: null,
        });
        onJobModalClose();
      } else {
        throw new Error(result.message || "Failed to submit CV");
      }
    } catch (error) {
      console.error("CV submission error:", error);
      toast({
        title: "CV Submission Failed",
        description: error?.message || "Server error. Please try again later.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsJobSubmitting(false);
    }
  };

  return (
    <Box
      bgImage="url('/png/career-footer.png')"
      bgSize="cover"
      bgPosition="center"
      bgRepeat="no-repeat"
      bgColor="#215560"
      minHeight="180px"
      pt={{ base: 6, md: 12 }}
      pb={0}
      mt={{ base: 10, md: 20 }}
      position="relative"
    >
      {/* Top: CTA */}
      <VStack
        spacing={{ base: 2, md: 4 }}
        mb={{ base: 4, md: 8 }}
        mx={{ base: 4, md: 8 }}
        position="relative"
        zIndex={1}
        color="white"
      >
        <Heading
          fontSize={{ base: "lg", md: "2xl", lg: "3xl" }}
          fontWeight="bold"
          textAlign="center"
        >
          {headerText}
        </Heading>
        <Text
          fontSize={{ base: "sm", md: "md", lg: "lg" }}
          textAlign="center"
          color="white"
          opacity={0.9}
        >
          {descriptionText}
        </Text>
        <HStack spacing={{ base: 2, md: 4 }} mt={{ base: 2, md: 4 }}>
          <Button
            onClick={onOpen}
            variant="outline"
            borderColor="#E6FF3F"
            color="#E6FF3F"
            borderRadius="full"
            px={{ base: 4, md: 7 }}
            py={{ base: 2, md: 6 }}
            fontWeight="bold"
            fontSize={{ base: "sm", md: "lg" }}
            _hover={{ bg: "#E6FF3F", color: "#215560", borderColor: "#E6FF3F" }}
          >
            {buttonText}
          </Button>
          {/* <Button
            onClick={onJobModalOpen}
            bg="#E6FF3F"
            color="#215560"
            borderRadius="full"
            px={{ base: 4, md: 7 }}
            py={{ base: 2, md: 6 }}
            fontWeight="bold"
            fontSize={{ base: 'sm', md: 'lg' }}
            _hover={{ bg: '#d4f02a' }}
          >
            Drop Your CV
          </Button> */}
        </HStack>
      </VStack>
      <Divider borderColor="#31777A" position="relative" zIndex={1} />
      {/* Bottom: Links & Copyright */}
      <Flex
        direction={{ base: "column", md: "row" }}
        align="center"
        justify="space-between"
        px={{ base: 2, md: 16 }}
        py={{ base: 2, md: 4 }}
        fontSize={{ base: "xs", md: "md" }}
        gap={{ base: 1, md: 2 }}
        position="relative"
        zIndex={1}
        color="white"
      >
        <HStack spacing={{ base: 3, md: 8 }} mb={{ base: 2, md: 0 }}>
          <Link
            href="/privacy-policy"
            color="white"
            _hover={{ textDecoration: "underline", color: "#E6FF3F" }}
          >
            Privacy Policy
          </Link>
          {/* <Link href="#" color="white" _hover={{ textDecoration: 'underline', color: '#E6FF3F' }}>Terms of Service</Link> */}
        </HStack>
        <Text
          flex={1}
          textAlign="center"
          color="white"
          opacity={0.8}
          fontSize={{ base: "xs", md: "sm" }}
        >
          © 2025 Business Automation Limited All rights reserved.
        </Text>
        <Box textAlign={{ base: "left", md: "right" }}>
          <HStack spacing={2} justify={{ base: "flex-start", md: "flex-end" }} align="center">
            <Text
              fontSize="sm"
              fontWeight="medium"
              opacity={0.9}
            >
              Our Presence Across Platforms
            </Text>

            <Link
              href="https://www.facebook.com/BusinessAutomationLtd"
              target="_blank"
              rel="noopener noreferrer"
              w="32px"
              h="32px"
              borderRadius="50%"
              bg="#4267B2"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ transform: "scale(1.1)", transition: "all 0.2s" }}
            >
              <Box color="white" fontSize="14px">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </Box>
            </Link>

            <Link
              href="https://bd.linkedin.com/company/business-automation-ltd"
              target="_blank"
              rel="noopener noreferrer"
              w="32px"
              h="32px"
              borderRadius="50%"
              bg="#0077B5"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ transform: "scale(1.1)", transition: "all 0.2s" }}
            >
              <Box color="white" fontSize="14px">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </Box>
            </Link>

            <Link
              href="https://www.youtube.com/@businessautomationltd.1998"
              target="_blank"
              rel="noopener noreferrer"
              w="32px"
              h="32px"
              borderRadius="50%"
              bg="#FF0000"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ transform: "scale(1.1)", transition: "all 0.2s" }}
            >
              <Box color="white" fontSize="14px">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </Box>
            </Link>
          </HStack>
        </Box>
        {/* <HStack spacing={{ base: 2, md: 5 }}>
          <Link href="https://wa.me/8801796946005" isExternal><Icon as={FaWhatsapp} boxSize={{ base: 4, md: 6 }} color="white" _hover={{ color: '#E6FF3F' }} /></Link>
          <Link href="https://www.linkedin.com/company/business-automation-ltd/" isExternal><Icon as={FaLinkedinIn} boxSize={{ base: 4, md: 6 }} color="white" _hover={{ color: '#E6FF3F' }} /></Link>
          <Link href="https://www.facebook.com/BusinessAutomationLtd" isExternal><Icon as={FaFacebookF} boxSize={{ base: 4, md: 6 }} color="white" _hover={{ color: '#E6FF3F' }} /></Link>
        </HStack> */}
      </Flex>

      {/* Subscription Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent
          mx={4}
          borderRadius="md"
          maxW={{ base: "95%", md: "700px" }} // full width on mobile, fixed on desktop
          bg="gray.100"
        >
          <ModalHeader textAlign="center" pb={2}>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
              Let's get started
            </Text>
          </ModalHeader>
          <ModalCloseButton color="gray.500" />

          <ModalBody pb={6}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={5}>
                {/* Name */}
                <FormControl isRequired>
                  <FormLabel color="black" fontWeight="medium" mb={1}>
                    Name
                  </FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Name"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: "blue.400", bg: "white" }}
                    size="md"
                  />
                </FormControl>

                {/* Email */}
                <FormControl isRequired>
                  <FormLabel color="black" fontWeight="medium" mb={1}>
                    Email
                  </FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="E-mail"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: "blue.400", bg: "white" }}
                    size="md"
                  />
                </FormControl>

                {/* Phone */}
                <FormControl isRequired>
                  <FormLabel color="black" fontWeight="medium" mb={1}>
                    Phone
                  </FormLabel>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: "blue.400", bg: "white" }}
                    size="md"
                  />
                </FormControl>

                {/* Submit Button */}
                <Button
                  type="submit"
                  bg="#214336"
                  color="white"
                  size="lg"
                  width="100%"
                  borderRadius="full"
                  isLoading={isSubmitting}
                  loadingText="Submitting..."
                  _hover={{ bg: "#193029" }}
                  mt={2}
                >
                  Submit
                </Button>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Job Application Modal */}
      <Modal isOpen={isJobModalOpen} onClose={onJobModalClose} isCentered>
        <ModalOverlay />
        <ModalContent
          mx={4}
          borderRadius="md"
          maxW={{ base: "95%", md: "700px" }}
          bg="gray.100"
        >
          <ModalHeader textAlign="center" pb={2}>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
              Let's get started
            </Text>
          </ModalHeader>
          <ModalCloseButton color="gray.500" />

          <ModalBody pb={6}>
            <form onSubmit={handleJobSubmit}>
              <VStack spacing={5}>
                {/* Name */}
                <FormControl isRequired>
                  <FormLabel color="black" fontWeight="medium" mb={1}>
                    Name
                  </FormLabel>
                  <Input
                    name="name"
                    value={jobFormData.name}
                    onChange={handleJobInputChange}
                    placeholder="Enter"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: "blue.400", bg: "white" }}
                    size="md"
                  />
                </FormControl>

                {/* Email */}
                <FormControl isRequired>
                  <FormLabel color="black" fontWeight="medium" mb={1}>
                    Email
                  </FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={jobFormData.email}
                    onChange={handleJobInputChange}
                    placeholder="Enter"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: "blue.400", bg: "white" }}
                    size="md"
                  />
                </FormControl>

                {/* Phone */}
                <FormControl isRequired>
                  <FormLabel color="black" fontWeight="medium" mb={1}>
                    Phone
                  </FormLabel>
                  <Input
                    name="phone"
                    type="tel"
                    value={jobFormData.phone}
                    onChange={handleJobInputChange}
                    placeholder="Enter"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: "blue.400", bg: "white" }}
                    size="md"
                  />
                </FormControl>

                {/* Interested Position */}
                <FormControl>
                  <FormLabel color="black" fontWeight="medium" mb={1}>
                    Interested Position
                  </FormLabel>

                  {/* Selected positions as tags */}
                  {jobFormData.positions.length > 0 && (
                    <Box mb={3}>
                      <Wrap spacing={2}>
                        {jobFormData.positions.map((position, index) => (
                          <WrapItem key={index}>
                            <Tag
                              size="md"
                              variant="solid"
                              colorScheme="blue"
                              borderRadius="full"
                            >
                              <TagLabel>{position}</TagLabel>
                              <TagCloseButton
                                onClick={() => removePosition(position)}
                              />
                            </Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>
                  )}

                  {/* Dropdown to add positions */}
                  <Select
                    name="positions"
                    value=""
                    onChange={handleJobInputChange}
                    placeholder="Select positions to add..."
                    bg="white"
                    border="1px solid"
                    borderColor={
                      jobFormData.positions.length === 0
                        ? "red.300"
                        : "gray.300"
                    }
                    _focus={{ borderColor: "blue.400", bg: "white" }}
                    size="md"
                  >
                    {positionOptions
                      .filter(
                        (option) => !jobFormData.positions.includes(option)
                      )
                      .map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                  </Select>

                  {jobFormData.positions.length === 0 && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      Please select at least one position you're interested in
                    </Text>
                  )}
                  {jobFormData.positions.length > 0 && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {jobFormData.positions.length} position(s) selected
                    </Text>
                  )}
                </FormControl>

                {/* Upload CV */}
                <FormControl isRequired>
                  <FormLabel color="black" fontWeight="medium" mb={1}>
                    Upload Your CV
                  </FormLabel>
                  <Flex align="center" gap={3}>
                    <Button
                      as="label"
                      htmlFor="cv-upload"
                      leftIcon={<FaUpload />}
                      size="sm"
                      bg="#214336"
                      color="white"
                      _hover={{ bg: "#2D3748" }}
                      cursor="pointer"
                      px={4}
                      py={2}
                      borderRadius="md"
                    >
                      Upload
                    </Button>
                    <Text fontSize="sm" color="gray.500" flex={1}>
                      {jobFormData.cv ? jobFormData.cv.name : "no file chosen"}
                    </Text>
                    <Input
                      id="cv-upload"
                      name="cv"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleJobInputChange}
                      display="none"
                    />
                  </Flex>
                </FormControl>

                {/* Submit Button */}
                <Button
                  type="submit"
                  bg="#214336"
                  color="white"
                  size="lg"
                  width="100%"
                  borderRadius="full"
                  isLoading={isJobSubmitting}
                  loadingText="Submitting..."
                  _hover={{ bg: "#193029" }}
                  mt={2}
                >
                  Submit
                </Button>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CareersFooter;
